import { Link } from "react-router-dom";
import { MapPin, MessageSquareText, Phone, ArrowRight } from "lucide-react";
import Seo, { breadcrumbs, webPageSchema, serviceSchema } from "../components/Seo";
import { SectionHeader } from "../components/UI";
import { siteConfig } from "../config";
import { ALL_CITY_SLUGS, CITY_DATA } from "../data/cityData";
import { CITY_IMAGES } from "../data/cityImages";

const GROUPS = [
  {
    label: "Suburb & Family Cities",
    test: "suburb-family",
    blurb: "Family SUVs, EVs, commuter vehicles, and lease/payment guidance.",
  },
  {
    label: "Affordable / Budget-Conscious",
    test: "city-budget",
    blurb: "Affordable new and used Chevy options, payment help, trade upgrades.",
  },
  {
    label: "Truck & Longer-Drive Cities",
    test: "truck-distance",
    blurb: "Trucks, used trucks, AWD SUVs, and longer-drive shopping support.",
  },
];

export default function CentralOhioHub() {
  const hero = CITY_IMAGES["central-ohio"] || CITY_IMAGES.default;

  return (
    <main id="main-content" data-testid="central-ohio-hub">
      <Seo
        title="Chevy Sales Help Across Central Ohio | Scott Sells Chevys"
        description="Personal Chevy buying help for drivers across Central Ohio — new and used vehicles, trade-in guidance, EV questions, and payment direction. Text Scott to start."
        canonicalPath="/chevy-sales-central-ohio"
        ogImage={hero.src}
        jsonLd={[
          breadcrumbs([{ name: "Home", path: "/" }, { name: "Central Ohio", path: "/chevy-sales-central-ohio" }]),
          webPageSchema({
            name: "Chevy Sales Help Across Central Ohio",
            path: "/chevy-sales-central-ohio",
            description: "Hub page linking to all Central Ohio cities where Scott Hall helps shoppers compare new and used Chevy vehicles, trades, EVs, and payment options.",
          }),
          serviceSchema({
            name: "Personal Chevy Buying Help Across Central Ohio",
            serviceType: "Personal vehicle shopping assistance",
            path: "/chevy-sales-central-ohio",
            description: "Scott helps drivers across Central Ohio shop new and used Chevy vehicles, value trade-ins, talk through payments, and answer EV questions — no pressure.",
          }),
        ]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[var(--ssc-navy)] text-white">
        <img src={hero.src} alt={hero.alt} loading="eager" width="1600" height="900" className="absolute inset-0 w-full h-full object-cover opacity-30" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "https://images.unsplash.com/photo-1577086664693-894d8405334a?auto=format&fit=crop&w=1600&q=70"; }} />
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--ssc-navy)]/95 via-[var(--ssc-navy)]/80 to-[var(--ssc-navy)]/95" />
        <div className="relative ssc-container py-12 md:py-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--ssc-blue)]/20 border border-[var(--ssc-blue)]/40 text-[var(--ssc-blue)] text-xs font-black uppercase tracking-wide">
            <MapPin size={14} /> Across Central Ohio
          </div>
          <h1 className="mt-3 text-3xl md:text-5xl font-extrabold leading-tight max-w-4xl ssc-display">
            Chevy Sales Help Across Central Ohio
          </h1>
          <p className="mt-4 text-base md:text-lg text-[var(--ssc-silver)] max-w-2xl leading-relaxed">
            Personal help for drivers across the Columbus metro and the surrounding cities. Whether you&apos;re shopping new Chevy SUVs in Dublin, used trucks near Circleville, or affordable used vehicles in Columbus, text Scott to start.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-primary"><MessageSquareText size={16} /> Text Scott</a>
            <a href={siteConfig.scott.phoneTel} className="ssc-btn ssc-btn-secondary"><Phone size={16} /> Call Scott</a>
            <Link to="/find-me-a-vehicle" className="ssc-btn ssc-btn-outline">Find Me a Vehicle</Link>
            <Link to="/value-my-trade" className="ssc-btn ssc-btn-outline">Value My Trade</Link>
          </div>
        </div>
      </section>

      {/* GROUPED CITIES */}
      {GROUPS.map(({ label, test, blurb }) => {
        const cities = ALL_CITY_SLUGS.filter((s) => CITY_DATA[s].bucket === test);
        return (
          <section key={test} className="ssc-section" data-testid={`hub-group-${test}`}>
            <div className="ssc-container">
              <SectionHeader title={label} subtitle={blurb} />
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {cities.map((s) => {
                  const d = CITY_DATA[s];
                  return (
                    <Link
                      key={s}
                      to={`/chevy-sales-${s}`}
                      className="ssc-card group hover:border-[var(--ssc-blue)] hover:shadow-md transition flex flex-col"
                      data-testid={`hub-city-${s}`}
                    >
                      <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">{d.city}, OH</div>
                      <h3 className="font-black text-lg text-[var(--ssc-navy)] mt-1 leading-tight">{d.h1.replace(/ Near .*$/, "")}</h3>
                      <p className="text-sm text-[var(--ssc-ink-2)] mt-2 leading-relaxed line-clamp-3">{d.heroSubhead}</p>
                      <div className="mt-auto pt-3 text-xs font-extrabold text-[var(--ssc-blue)] inline-flex items-center gap-1">
                        See {d.city} page <ArrowRight size={12} className="group-hover:translate-x-0.5 transition" />
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })}

      {/* FINAL */}
      <section className="ssc-section bg-[var(--ssc-silver-2)]">
        <div className="ssc-container max-w-3xl text-center">
          <h2 className="text-2xl md:text-3xl font-black text-[var(--ssc-navy)]">Don&apos;t See Your City?</h2>
          <p className="mt-3 text-[var(--ssc-ink)] leading-relaxed">
            Scott helps drivers from across the Central Ohio region — not just the cities listed here. Text him with where you&apos;re shopping from and what you&apos;re looking for.
          </p>
          <div className="mt-5 flex flex-wrap gap-2 justify-center">
            <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-primary"><MessageSquareText size={16} /> Text Scott</a>
            <Link to="/find-me-a-vehicle" className="ssc-btn ssc-btn-outline">Find Me a Vehicle</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
